/**
 * Site-wide premium scroll experience (performance-first):
 * - GSAP ScrollTrigger reveals (text + product cards)
 * - Optional Lenis smooth scroll (desktop, non-PDP)
 * - SplitType line stagger only for multi-line marketing paragraphs
 *
 * Heavy libs load after first paint via dynamic import() + idle callback.
 * Loaded when ShopifyScrollReveal.enabled (theme setting).
 */
import { ThemeEvents } from '@theme/events';
import { onDocumentReady } from '@theme/utilities';

const CARD_SELECTOR = '.product-grid__item, .resource-list__item';
/** Content text — product buy-box / tips filtered in shouldSkipText */
const TEXT_SELECTOR = [
  '#MainContent .text-block',
  '#MainContent .locale-banner__title',
  '#MainContent .locale-banner__subtitle',
  '#MainContent .community-carousel__title',
  '#MainContent .community-carousel__eyebrow',
  '#MainContent .community-carousel__header .rte',
  '#MainContent .section-resource-list__header .text-block',
].join(', ');

/** PDP: pairs-well / recommendation headers only — skip description & tips SplitType */
const PRODUCT_TEXT_SELECTOR = [
  '#MainContent .section-resource-list__header .text-block',
  '#MainContent .product-recommendations .section-resource-list__content > .text-block',
  '#MainContent .locale-banner__title',
  '#MainContent .community-carousel__title',
].join(', ');

const CARD_DONE_ATTR = 'data-scroll-reveal-done';
const TEXT_DONE_ATTR = 'data-text-reveal-done';
const ARMED_ATTR = 'data-reveal-armed';
const PENDING_CLASS = 'scroll-reveal-pending';
const LENIS_CLASS = 'has-smooth-scroll';
const LINE_CLASS = 'scroll-reveal-line';
const SPLIT_ATTR = 'data-text-reveal-split';

const HEADING_SELECTOR =
  '.h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6, .locale-banner__title, .community-carousel__title';

const CDN = {
  gsap: 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/+esm',
  scrollTrigger: 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/ScrollTrigger.js/+esm',
  lenis: 'https://cdn.jsdelivr.net/npm/lenis@1.3.4/+esm',
  splitType: 'https://cdn.jsdelivr.net/npm/split-type@0.3.4/+esm',
};

/** @type {typeof import('gsap').gsap | null} */
let gsap = null;
/** @type {typeof import('gsap/ScrollTrigger').ScrollTrigger | null} */
let ScrollTrigger = null;
/** @type {typeof import('lenis').default | null} */
let LenisCtor = null;
/** @type {typeof import('split-type').default | null} */
let SplitTypeCtor = null;

/** @type {any} */
let matchMediaContext = null;
/** @type {MutationObserver | null} */
let gridObserver = null;
/** @type {MutationObserver | null} */
let scrollLockObserver = null;
/** @type {((event: Event) => void) | null} */
let filterUpdateHandler = null;
/** @type {((event: Event) => void) | null} */
let resizeHandler = null;
/** @type {InstanceType<NonNullable<typeof LenisCtor>> | null} */
let lenis = null;
/** @type {((time: number) => void) | null} */
let lenisTicker = null;
/** @type {Map<Element, { revert: () => void, lines?: Element[] }>} */
const splitInstances = new Map();

let libsPromise = null;
let splitTypePromise = null;
let booted = false;

/**
 * @returns {boolean}
 */
function isProductTemplate() {
  const fromConfig = window.ShopifyScrollReveal?.template;
  const name = fromConfig || window.Theme?.template?.name || '';
  return String(name).startsWith('product');
}

/**
 * @returns {string}
 */
function getTextSelector() {
  return isProductTemplate() ? PRODUCT_TEXT_SELECTOR : TEXT_SELECTOR;
}

/**
 * Lenis only on desktop, non-product pages, when theme enables smooth scroll.
 * @returns {boolean}
 */
function shouldUseLenis() {
  const config = window.ShopifyScrollReveal;
  if (config?.smoothScroll === false) return false;
  if (isProductTemplate()) return false;
  // Desktop only — avoid touch/jank on phones & small tablets
  if (!window.matchMedia('(min-width: 990px)').matches) return false;
  if (window.matchMedia('(pointer: coarse)').matches) return false;
  return true;
}

/**
 * Load GSAP + ScrollTrigger once. Lenis/SplitType load on demand.
 * @returns {Promise<void>}
 */
function loadCoreLibs() {
  if (libsPromise) return libsPromise;

  libsPromise = Promise.all([
    import(/* webpackIgnore: true */ CDN.gsap),
    import(/* webpackIgnore: true */ CDN.scrollTrigger),
  ]).then(([gsapMod, stMod]) => {
    gsap = gsapMod.gsap || gsapMod.default || gsapMod;
    ScrollTrigger = stMod.ScrollTrigger || stMod.default || stMod;
    gsap.registerPlugin(ScrollTrigger);
  });

  return libsPromise;
}

/**
 * @returns {Promise<typeof LenisCtor>}
 */
async function loadLenis() {
  if (LenisCtor) return LenisCtor;
  const mod = await import(/* webpackIgnore: true */ CDN.lenis);
  LenisCtor = mod.default || mod;
  return LenisCtor;
}

/**
 * @returns {Promise<typeof SplitTypeCtor>}
 */
async function loadSplitType() {
  if (SplitTypeCtor) return SplitTypeCtor;
  if (!splitTypePromise) {
    splitTypePromise = import(/* webpackIgnore: true */ CDN.splitType).then((mod) => {
      SplitTypeCtor = mod.default || mod;
      return SplitTypeCtor;
    });
  }
  return splitTypePromise;
}

/**
 * @param {() => void} fn
 * @param {number} [timeoutMs]
 */
function whenIdle(fn, timeoutMs = 800) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => fn(), { timeout: timeoutMs });
  } else {
    window.setTimeout(fn, Math.min(timeoutMs, 200));
  }
}

/**
 * Prefer waiting for first paint, then idle, before heavy setup.
 * @param {() => void | Promise<void>} fn
 */
function afterFirstPaint(fn) {
  let started = false;
  const run = () => {
    if (started) return;
    started = true;
    whenIdle(() => {
      Promise.resolve(fn()).catch((error) => {
        console.error('[scroll-reveal] init failed', error);
        document.documentElement.classList.remove(PENDING_CLASS);
      });
    }, 800);
  };

  if (document.readyState === 'complete') {
    requestAnimationFrame(() => requestAnimationFrame(run));
  } else {
    window.addEventListener('load', () => requestAnimationFrame(() => requestAnimationFrame(run)), {
      once: true,
    });
  }

  // Cap wait so slow assets don't delay reveals forever
  window.setTimeout(run, 1600);
}

/**
 * @param {number} thresholdPercent
 * @returns {string}
 */
function thresholdToStart(thresholdPercent) {
  const viewportLine = Math.max(55, Math.min(95, 100 - thresholdPercent));
  return `top ${viewportLine}%`;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function shouldSkipText(el) {
  if (el.closest('jumbo-text, .jumbo-text__container')) return true;
  if (el.closest('product-card, .product-card, .product-grid__item, .resource-list__item')) return true;
  if (
    el.closest(
      '.hero__entry-fade, [data-text-reveal-skip], header, .header, #header-group, footer, .cart-drawer, .menu-drawer, .product-details, product-information, .accordion, details, .disclosure-content, .product__description'
    )
  ) {
    return true;
  }
  if (el.matches('p') && el.closest('.text-block, .locale-banner__subtitle, .community-carousel__eyebrow')) {
    return true;
  }
  if (isEffectivelyHidden(el)) return true;
  const text = (el.textContent || '').trim();
  if (text.length > 0 && text.length < 4) return true;
  return false;
}

/**
 * @param {Element} el
 */
function armReveal(el) {
  el.setAttribute(ARMED_ATTR, '');
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function shouldSplitIntoLines(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (isProductTemplate()) return false;
  if (!SplitTypeCtor && !splitTypePromise) {
    // Decision made before SplitType loads — only split marketing paragraphs
  }
  if (isEffectivelyHidden(el)) return false;
  if (el.matches(HEADING_SELECTOR)) return false;
  if (/\bh[1-6]\b/.test(el.className || '')) return false;

  const isParagraphType =
    el.matches('.text-block.paragraph, .text-block.rte, .locale-banner__subtitle, .community-carousel__eyebrow') ||
    (el.matches('p') && Boolean(el.closest('.rte, .text-block')));

  if (!isParagraphType) return false;
  return estimateLineCount(el) > 1;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isEffectivelyHidden(el) {
  if (!(el instanceof HTMLElement)) return true;
  if (el.closest('[hidden]')) return true;
  if (el.getClientRects().length === 0) return true;
  const style = el.style;
  if (style.display === 'none' || style.visibility === 'hidden') return true;
  return false;
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function estimateLineCount(el) {
  const fontSize = parseFloat(getComputedStyle(el).fontSize) || 16;
  const height = el.getBoundingClientRect().height;
  if (height < 2) return 1;
  return Math.max(1, Math.round(height / (fontSize * 1.35)));
}

/**
 * @param {Element} el
 * @param {string} attr
 */
function markDone(el, attr) {
  el.setAttribute(attr, '');
}

/**
 * @param {Element} el
 * @param {string} doneAttr
 */
function forceShow(el, doneAttr) {
  if (!gsap) {
    markDone(el, doneAttr);
    if (el instanceof HTMLElement) {
      el.style.opacity = '1';
      el.style.filter = 'none';
      el.style.transform = 'none';
      el.style.visibility = 'visible';
    }
    return;
  }
  markDone(el, doneAttr);
  gsap.set(el, { autoAlpha: 1, y: 0, clearProps: 'filter,transform' });
  el.querySelectorAll(`.${LINE_CLASS}`).forEach((line) => {
    gsap.set(line, { autoAlpha: 1, y: 0, clearProps: 'filter,transform' });
  });
}

function ensureRevealedVisible() {
  if (!gsap) return;

  document.querySelectorAll(`[${TEXT_DONE_ATTR}]`).forEach((el) => {
    const style = getComputedStyle(el);
    if (style.opacity === '0' || style.visibility === 'hidden') {
      gsap.set(el, { autoAlpha: 1, y: 0, clearProps: 'filter' });
    }
    el.querySelectorAll(`.${LINE_CLASS}`).forEach((line) => {
      const lineStyle = getComputedStyle(line);
      if (lineStyle.opacity === '0' || lineStyle.visibility === 'hidden') {
        gsap.set(line, { autoAlpha: 1, y: 0, clearProps: 'filter' });
      }
    });
  });

  document.querySelectorAll(`[${CARD_DONE_ATTR}]`).forEach((el) => {
    const style = getComputedStyle(el);
    if (style.opacity === '0' || style.visibility === 'hidden') {
      gsap.set(el, { autoAlpha: 1, y: 0 });
    }
  });
}

function forceUnhideStuckInView() {
  const vh = window.innerHeight || document.documentElement.clientHeight;

  /** @param {Element} el */
  const inView = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < vh * 0.98 && rect.bottom > vh * 0.02;
  };

  document.querySelectorAll(`[${ARMED_ATTR}]:not([${TEXT_DONE_ATTR}])`).forEach((el) => {
    if (el.matches(CARD_SELECTOR)) return;
    if (!inView(el)) return;
    forceShow(el, TEXT_DONE_ATTR);
  });

  document.querySelectorAll(`${CARD_SELECTOR}[${ARMED_ATTR}]:not([${CARD_DONE_ATTR}])`).forEach((el) => {
    if (!inView(el)) return;
    forceShow(el, CARD_DONE_ATTR);
  });
}

/**
 * @param {HTMLElement} el
 * @returns {Promise<HTMLElement[]>}
 */
async function splitElementLines(el) {
  if (el.matches?.(HEADING_SELECTOR) || /\bh[1-6]\b/.test(el.className || '')) {
    return [];
  }

  const SplitType = await loadSplitType();
  if (!SplitType) return [];

  const existing = splitInstances.get(el);
  if (existing) {
    existing.revert();
    splitInstances.delete(el);
    el.removeAttribute(SPLIT_ATTR);
  }

  const split = new SplitType(el, {
    types: 'lines',
    tagName: 'span',
    lineClass: LINE_CLASS,
  });

  splitInstances.set(el, split);
  el.setAttribute(SPLIT_ATTR, '');

  el.setAttribute('aria-label', (el.textContent || '').replace(/\s+/g, ' ').trim());
  split.lines?.forEach((line) => {
    if (line instanceof HTMLElement) {
      line.setAttribute('aria-hidden', 'true');
    }
  });

  return /** @type {HTMLElement[]} */ (split.lines || []);
}

/**
 * Mark above-the-fold targets as done without ever hiding them (protects LCP).
 * @param {string} textSelector
 */
function markAboveFoldDone(textSelector) {
  const vh = window.innerHeight || document.documentElement.clientHeight;

  document.querySelectorAll(CARD_SELECTOR).forEach((el) => {
    if (el.hasAttribute(CARD_DONE_ATTR)) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < vh * 0.92 && rect.bottom > 0) {
      markDone(el, CARD_DONE_ATTR);
    }
  });

  document.querySelectorAll(textSelector).forEach((el) => {
    if (el.hasAttribute(TEXT_DONE_ATTR) || shouldSkipText(el)) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < vh * 0.92 && rect.bottom > 0) {
      markDone(el, TEXT_DONE_ATTR);
    }
  });
}

/**
 * @param {Element[]} elements
 * @param {{ start: string }} config
 */
function registerCardBatch(elements, config) {
  if (!gsap || !ScrollTrigger) return;

  // Skip already-done (above-fold) and already-armed cards
  const pending = elements.filter(
    (el) => !el.hasAttribute(CARD_DONE_ATTR) && !el.hasAttribute(ARMED_ATTR)
  );
  if (pending.length === 0) return;

  pending.forEach((el) => armReveal(el));
  gsap.set(pending, { autoAlpha: 0, y: 16 });

  ScrollTrigger.batch(pending, {
    once: true,
    start: config.start,
    onEnter: (batch) => {
      const toReveal = batch.filter((el) => !el.hasAttribute(CARD_DONE_ATTR));
      if (toReveal.length === 0) return;

      toReveal.forEach((el) => markDone(el, CARD_DONE_ATTR));
      gsap.to(toReveal, {
        autoAlpha: 1,
        y: 0,
        duration: 0.85,
        ease: 'power1.out',
        stagger: 0.04,
        overwrite: true,
      });
    },
  });
}

/**
 * @param {Element[]} elements
 * @param {{ start: string }} config
 */
function registerWholeTextBatch(elements, config) {
  if (!gsap || !ScrollTrigger) return;

  const pending = elements.filter((el) => !el.hasAttribute(TEXT_DONE_ATTR));
  if (pending.length === 0) return;

  pending.forEach((el) => armReveal(el));
  gsap.set(pending, { autoAlpha: 0, filter: 'blur(12px)', y: 10 });

  ScrollTrigger.batch(pending, {
    once: true,
    start: config.start,
    onEnter: (batch) => {
      gsap.to(batch, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.85,
        ease: 'power2.out',
        stagger: 0.06,
        overwrite: true,
        onStart: () => {
          batch.forEach((el) => markDone(el, TEXT_DONE_ATTR));
        },
        onComplete: () => {
          gsap.set(batch, { clearProps: 'filter' });
        },
      });
    },
  });
}

/**
 * @param {HTMLElement[]} elements
 * @param {{ start: string }} config
 */
async function registerLineTextBatch(elements, config) {
  if (!gsap || !ScrollTrigger) return;

  const pending = elements.filter((el) => !el.hasAttribute(TEXT_DONE_ATTR));
  if (pending.length === 0) return;

  for (const el of pending) {
    if (estimateLineCount(el) <= 1) {
      registerWholeTextBatch([el], config);
      continue;
    }

    const lines = await splitElementLines(el);
    if (lines.length <= 1) {
      const split = splitInstances.get(el);
      if (split) {
        split.revert();
        splitInstances.delete(el);
      }
      el.removeAttribute(SPLIT_ATTR);
      el.removeAttribute('aria-label');
      registerWholeTextBatch([el], config);
      continue;
    }

    armReveal(el);
    gsap.set(el, { autoAlpha: 1, clearProps: 'filter,transform' });
    gsap.set(lines, { autoAlpha: 0, filter: 'blur(12px)', y: 12 });

    ScrollTrigger.create({
      trigger: el,
      once: true,
      start: config.start,
      onEnter: () => {
        const currentLines = splitInstances.get(el)?.lines || lines;
        gsap.to(currentLines, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.08,
          overwrite: true,
          onStart: () => markDone(el, TEXT_DONE_ATTR),
          onComplete: () => {
            gsap.set(currentLines, { clearProps: 'filter' });
          },
        });
      },
    });
  }
}

/**
 * @param {Element[]} elements
 * @param {{ start: string }} config
 */
async function registerTextBatch(elements, config) {
  const candidates = elements.filter((el) => !el.hasAttribute(TEXT_DONE_ATTR) && !shouldSkipText(el));
  if (candidates.length === 0) return;

  /** @type {HTMLElement[]} */
  const lineBlocks = [];
  /** @type {Element[]} */
  const wholeBlocks = [];

  for (const el of candidates) {
    if (el instanceof HTMLElement && shouldSplitIntoLines(el)) {
      lineBlocks.push(el);
    } else {
      wholeBlocks.push(el);
    }
  }

  registerWholeTextBatch(wholeBlocks, config);
  if (lineBlocks.length > 0) {
    await registerLineTextBatch(lineBlocks, config);
  }
}

/**
 * @param {{ start: string }} config
 */
function revealInViewNow(config) {
  if (!gsap) return;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const textSelector = getTextSelector();

  const cards = [...document.querySelectorAll(CARD_SELECTOR)].filter((el) => {
    if (el.hasAttribute(CARD_DONE_ATTR)) return false;
    const rect = el.getBoundingClientRect();
    return rect.top < vh * 0.95 && rect.bottom > 0;
  });
  if (cards.length > 0) {
    cards.forEach((el) => {
      armReveal(el);
      markDone(el, CARD_DONE_ATTR);
    });
    gsap.to(cards, {
      autoAlpha: 1,
      y: 0,
      duration: 0.85,
      ease: 'power1.out',
      stagger: 0.04,
      overwrite: true,
    });
  }

  const texts = [...document.querySelectorAll(textSelector)].filter((el) => {
    if (el.hasAttribute(TEXT_DONE_ATTR) || shouldSkipText(el)) return false;
    const rect = el.getBoundingClientRect();
    return rect.top < vh * 0.95 && rect.bottom > 0;
  });

  for (const el of texts) {
    armReveal(el);
    markDone(el, TEXT_DONE_ATTR);

    if (el instanceof HTMLElement && el.hasAttribute(SPLIT_ATTR)) {
      const lines = splitInstances.get(el)?.lines || el.querySelectorAll(`.${LINE_CLASS}`);
      gsap.to(lines, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.75,
        ease: 'power2.out',
        stagger: 0.06,
        overwrite: true,
        onComplete: () => gsap.set(lines, { clearProps: 'filter' }),
      });
    } else {
      gsap.to(el, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.75,
        ease: 'power2.out',
        overwrite: true,
        clearProps: 'filter',
      });
    }
  }

  void config;
}

function syncLenisWithScrollLock() {
  if (!lenis) return;
  if (document.documentElement.hasAttribute('scroll-lock')) {
    lenis.stop();
  } else {
    lenis.start();
  }
}

async function initSmoothScroll() {
  destroySmoothScroll();
  if (!shouldUseLenis() || !gsap || !ScrollTrigger) return;

  const Lenis = await loadLenis();
  if (!Lenis) return;

  document.documentElement.classList.add(LENIS_CLASS);

  lenis = new Lenis({
    lerp: 0.1,
    wheelMultiplier: 0.95,
    touchMultiplier: 1,
    smoothWheel: true,
    autoRaf: false,
    allowNestedScroll: true,
  });

  lenis.on('scroll', ScrollTrigger.update);

  let stuckCheckTimer = 0;
  lenis.on('scroll', () => {
    window.clearTimeout(stuckCheckTimer);
    stuckCheckTimer = window.setTimeout(forceUnhideStuckInView, 500);
  });

  lenisTicker = (time) => {
    lenis?.raf(time * 1000);
  };
  gsap.ticker.add(lenisTicker);
  gsap.ticker.lagSmoothing(0);

  scrollLockObserver = new MutationObserver(syncLenisWithScrollLock);
  scrollLockObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock'],
  });
  syncLenisWithScrollLock();
}

function destroySmoothScroll() {
  scrollLockObserver?.disconnect();
  scrollLockObserver = null;

  if (lenisTicker && gsap) {
    gsap.ticker.remove(lenisTicker);
    lenisTicker = null;
  }

  if (lenis) {
    lenis.destroy();
    lenis = null;
  }

  document.documentElement.classList.remove(LENIS_CLASS);
  if (gsap) gsap.ticker.lagSmoothing(500, 33);
}

function revertAllSplits() {
  splitInstances.forEach((split, el) => {
    try {
      split.revert();
    } catch {
      /* ignore */
    }
    el.removeAttribute(SPLIT_ATTR);
    el.removeAttribute('aria-label');
  });
  splitInstances.clear();
}

/**
 * @param {{ start: string }} config
 */
async function resplitPendingParagraphs(config) {
  if (!gsap || !ScrollTrigger || isProductTemplate()) return;

  const textSelector = getTextSelector();
  const pending = [...document.querySelectorAll(`${textSelector}[${SPLIT_ATTR}]:not([${TEXT_DONE_ATTR}])`)];

  for (const el of pending) {
    if (!(el instanceof HTMLElement)) continue;
    ScrollTrigger.getAll().forEach((trigger) => {
      if (trigger.trigger === el) trigger.kill();
    });
    el.removeAttribute(TEXT_DONE_ATTR);
    const lines = await splitElementLines(el);
    if (lines.length <= 1) {
      gsap.set(el, { autoAlpha: 0, filter: 'blur(12px)', y: 10 });
      registerWholeTextBatch([el], config);
      continue;
    }
    gsap.set(el, { autoAlpha: 1, clearProps: 'filter,transform' });
    gsap.set(lines, { autoAlpha: 0, filter: 'blur(12px)', y: 12 });
    armReveal(el);
    ScrollTrigger.create({
      trigger: el,
      once: true,
      start: config.start,
      onEnter: () => {
        const currentLines = splitInstances.get(el)?.lines || lines;
        gsap.to(currentLines, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.08,
          overwrite: true,
          onStart: () => markDone(el, TEXT_DONE_ATTR),
          onComplete: () => gsap.set(currentLines, { clearProps: 'filter' }),
        });
      },
    });
  }

  ScrollTrigger.refresh();
}

/**
 * @param {{ threshold?: number }} [options]
 */
async function initScrollReveal(options = {}) {
  if (booted) return;
  await loadCoreLibs();
  if (!gsap || !ScrollTrigger) return;

  const threshold = options.threshold ?? 20;
  const config = { start: thresholdToStart(threshold) };
  const textSelector = getTextSelector();

  matchMediaContext?.revert();
  matchMediaContext = gsap.matchMedia();

  matchMediaContext.add('(prefers-reduced-motion: reduce)', () => {
    document.documentElement.classList.remove(PENDING_CLASS);
    destroySmoothScroll();
    revertAllSplits();
    document.querySelectorAll(CARD_SELECTOR).forEach((el) => markDone(el, CARD_DONE_ATTR));
    document.querySelectorAll(textSelector).forEach((el) => {
      if (!shouldSkipText(el)) markDone(el, TEXT_DONE_ATTR);
    });
    gsap.set(CARD_SELECTOR, { autoAlpha: 1, y: 0, clearProps: 'transform' });
    gsap.set(textSelector, { autoAlpha: 1, y: 0, clearProps: 'filter,transform' });
    booted = true;
    window.ShopifyScrollReveal = {
      ...(window.ShopifyScrollReveal || {}),
      booted: true,
    };
  });

  matchMediaContext.add('(prefers-reduced-motion: no-preference)', () => {
    document.documentElement.classList.add(PENDING_CLASS);

    // Lenis is async / optional — don't block reveals on it
    void initSmoothScroll();

    // Keep first paint content visible — never hide above-fold targets
    markAboveFoldDone(textSelector);

    registerCardBatch([...document.querySelectorAll(CARD_SELECTOR)], config);
    void registerTextBatch([...document.querySelectorAll(textSelector)], config).then(() => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        ensureRevealedVisible();
        forceUnhideStuckInView();

        window.setTimeout(() => {
          ScrollTrigger.refresh();
          ensureRevealedVisible();
          forceUnhideStuckInView();
        }, 800);

        window.setTimeout(forceUnhideStuckInView, 2500);
      });
    });

    if (!gridObserver) {
      /** @type {MutationRecord[]} */
      let pendingMutations = [];
      let mutationTimer = 0;

      const flushMutations = () => {
        const mutations = pendingMutations;
        pendingMutations = [];
        mutationTimer = 0;

        /** @type {Element[]} */
        const addedCards = [];
        /** @type {Element[]} */
        const addedText = [];

        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;

            if (node.matches(CARD_SELECTOR)) {
              addedCards.push(node);
            } else {
              addedCards.push(...node.querySelectorAll(CARD_SELECTOR));
            }

            try {
              if (node.matches(textSelector)) addedText.push(node);
            } catch {
              /* compound selector */
            }
            addedText.push(...node.querySelectorAll(textSelector));
          }
        }

        let changed = false;
        if (addedCards.length > 0) {
          registerCardBatch(addedCards, config);
          changed = true;
        }
        if (addedText.length > 0) {
          void registerTextBatch(addedText, config);
          changed = true;
        }
        if (changed) {
          ScrollTrigger.refresh();
          requestAnimationFrame(() => {
            revealInViewNow(config);
            ensureRevealedVisible();
            forceUnhideStuckInView();
          });
        }
      };

      gridObserver = new MutationObserver((mutations) => {
        pendingMutations.push(...mutations);
        window.clearTimeout(mutationTimer);
        mutationTimer = window.setTimeout(flushMutations, 150);
      });
      const observeRoot = document.getElementById('MainContent') || document.body;
      gridObserver.observe(observeRoot, { childList: true, subtree: true });
    }

    filterUpdateHandler = () => {
      requestAnimationFrame(() => {
        registerCardBatch([...document.querySelectorAll(CARD_SELECTOR)], config);
        void registerTextBatch([...document.querySelectorAll(textSelector)], config);
        ScrollTrigger.refresh();
      });
    };
    document.addEventListener(ThemeEvents.FilterUpdate, filterUpdateHandler);

    let resizeTimer = 0;
    resizeHandler = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        void resplitPendingParagraphs(config);
      }, 250);
    };
    window.addEventListener('resize', resizeHandler);

    booted = true;
    window.ShopifyScrollReveal = {
      ...(window.ShopifyScrollReveal || {}),
      booted: true,
    };

    return () => {
      gridObserver?.disconnect();
      gridObserver = null;
      if (filterUpdateHandler) {
        document.removeEventListener(ThemeEvents.FilterUpdate, filterUpdateHandler);
        filterUpdateHandler = null;
      }
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
      }
      destroySmoothScroll();
      revertAllSplits();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      document.documentElement.classList.remove(PENDING_CLASS);
      booted = false;
    };
  });
}

onDocumentReady(() => {
  const config = window.ShopifyScrollReveal;
  if (!config?.enabled) return;

  // Never block first paint — schedule after load/idle
  afterFirstPaint(() => initScrollReveal({ threshold: config.threshold }));
});

window.addEventListener(
  'pagehide',
  () => {
    matchMediaContext?.revert();
    matchMediaContext = null;
    destroySmoothScroll();
    revertAllSplits();
  },
  { once: true }
);
